import {featureTypes, dataTypes} from "./Constants";
import BaseService from './BaseService';
import UserStorage from "../storage/UserStorage";
import UrlParamsService from "./UrlParamsService";
import LocationData from '../data/LocationData';
import type { Coordinate } from '../data/LocationData';
import PlaceData from '../data/PlaceData';
import GeoBounds from "../data/GeoBounds";
import { Geolocation } from '@capacitor/geolocation';
import AppService from "./AppService";
import type { LngLatBounds } from 'mapbox-gl';

/**
 * Rangos de zoom en los que cada tipo es visible.
 *
 * La mayoria son rangos [min, max]; hotel_names y route_names son un unico
 * umbral minimo.
 */
export interface VisibilityZoomLevels {
    route: number[];
    stage: number[];
    place: number[];
    hotel: number[];
    hotel_names: number;
    poi: number[];
    route_names: number;
}

/**
 * Claves de VisibilityZoomLevels cuyo valor es un rango [min, max].
 *
 * El array es la unica fuente: de el sale el tipo y la comprobacion en runtime
 * de isZoomRangeType, asi no se pueden desincronizar.
 */
export const zoomRangeTypes = ['route', 'stage', 'place', 'hotel', 'poi'] as const;
export type ZoomRangeType = typeof zoomRangeTypes[number];

/** Coordenadas tal y como las devuelve getCurrentLocation. */
export interface LatLng {
    lat: number;
    lng: number;
}

/**
 * Provides different geo-related utilities.
 * - Parse geo data from URL.
 * - Check visibility for zoom level, object type.
 */
class GeoService extends BaseService {
    static typeHotel = 'hotel';
    static typeLocation = 'place';

    /** Memoizado: false cuando la URL no trae localizacion. */
    static locationFromUrl: LocationData | false | undefined;

    /** Memoizado: false cuando la URL no trae feature. */
    static placeFromUrl: PlaceData | false | undefined;

    /**
     * Reset the memoized URL-derived location & place so they are recomputed
     * from UrlParamsService. Used when a new deep link arrives while the app
     * is already running (warm start).
     */
    static resetUrlParamsCache(): void {
        this.locationFromUrl = undefined;
        this.placeFromUrl = undefined;
    }

    /**
     * Get default zoom for object type / feature type.
     */
    static getTypeZoom(type: string): number {
        const config = this.config();

        switch (type) {
            case 'hotel':
            case featureTypes.albergue:
                return config.zoom_level_accommodation_min + 0.1;
            case 'place':
            case featureTypes.localidad:
            default:
                return config.zoom_level_place_min + 0.1;
        }
    }

    /**
     * Get map bounds from config.
     *
     * @return Containing two arrays:
     * - Southwest coordinates
     * - Northeast coordinates
     */
    static getMapBounds(): number[][] {
        const config = this.config();
        return [
            config.map_bounds_sw,
            config.map_bounds_ne
        ];
    }

    /**
     * Gets initial location / zoom.
     *
     * Dependes on vertical / horizontal
     */
    static getInitLocation(): LocationData {
        const config = this.config();
        if (window.innerHeight > window.innerWidth) {
            return new LocationData(config.init_lat_vr, config.init_lon_vr, config.init_zoom_vr);
        } else {
            return new LocationData(config.init_lat_hz, config.init_lon_hz, config.init_zoom_hz);
        }
    }


    /**
     * Gets user position if stored.
     */
    static getStoredLocation(): LocationData | null {
        const lon = UserStorage.getItem('gronze_lon');
        const lat = UserStorage.getItem('gronze_lat');
        const zoom = UserStorage.getItem('gronze_zoom');

        if (lon && lat && zoom && lon !== 'undefined' && lat !== 'undefined') {
            return new LocationData(lat, lon, zoom);
        }
        else {
            return null;
        }
    }

    /**
     * Get location from Url.
     *
     * Zoom value defaults to config.init_zoom_with_coords
     */
    static getLocationFromUrl(): LocationData | false {
        if (typeof this.locationFromUrl === 'undefined') {
            const params = UrlParamsService.getParams();
            if (params.lon && params.lat) {
                let zoom = params.zoom ? parseFloat(params.zoom) : this.config().init_zoom_with_coords;
                // Keep the URL zoom within the range where the referenced feature
                // type is visible; clamp it to the nearest visible zoom otherwise.
                const dataType = UrlParamsService.getDataType();
                if (params.zoom && dataType) {
                    zoom = this.clampZoomToVisibility(dataType, zoom);
                }
                this.locationFromUrl = new LocationData(params.lat, params.lon, zoom);
            }
            else {
                this.locationFromUrl = false;
            }
        }
        return this.locationFromUrl;
    }

    /**
     * Get place from Url
     */
    static getPlaceFromUrl(): PlaceData | false {
        if (typeof this.placeFromUrl === 'undefined') {
            if (UrlParamsService.hasFeature()) {
                // hasFeature() ya garantiza que hay id y que el tipo es valido.
                this.placeFromUrl = new PlaceData(UrlParamsService.getDataType() as string, UrlParamsService.id as string);
            }
            else {
                this.placeFromUrl = false;
            }
        }
        return this.placeFromUrl;
    }

    /**
     * Gets initial position, by order of precedence:
     * - Position from the URL (deep link)
     * - Last stored position
     * - Configured initial location
     *
     * Deliberately independent from the city list: that list only feeds the city
     * search and is downloaded in the background once authenticated, so the map
     * must not wait for it nor fail with it (@see App.loadCityList).
     *
     * Same precedence as isUsingConfigInitialLocation(), which App uses to decide
     * whether to show the initial location toast.
     */
    static getInitialPosition(): LocationData {
        return this.getLocationFromUrl() || this.getStoredLocation() || this.getInitLocation();
    }

    /**
     * Gets zoom levels as structured data.
     *
     * For every data type we have [min, max]
     */
    static getVisibilityZoomLevels(): VisibilityZoomLevels {
        const config = this.config();

        return {
            route: config.zoom_level_route,
            stage: config.zoom_level_stage,
            place: [config.zoom_level_place_min, config.zoom_level_place_max],
            hotel: [config.zoom_level_accommodation_min, config.zoom_level_max ],
            hotel_names: config.zoom_level_accommodation_text_min,
            poi: [config.zoom_level_accommodation_min, config.zoom_level_max ],
            route_names: config.zoom_level_route_names_min,
        }
    }

    /**
     * True si el tipo tiene un rango [min, max] y admite getTypeZoomLevels.
     *
     * Deja fuera hotel_names y route_names, que son un umbral suelto, y los
     * tipos de dato sin rango de visibilidad (track, page).
     */
    static isZoomRangeType(type: string): type is ZoomRangeType {
        return (zoomRangeTypes as readonly string[]).includes(type);
    }

    /**
     * Gets max zoom level for data type.
     *
     * Solo admite los tipos cuyo valor es un rango: desestructurar
     * hotel_names o route_names, que son numeros, lanzaria TypeError. El tipo
     * del parametro lo garantiza; para un valor que solo se conoce en runtime,
     * filtra antes con isZoomRangeType.
     */
    static getTypeZoomLevels(type: ZoomRangeType): {min: number, max: number} {
        const levels = this.getVisibilityZoomLevels();
        const [min, max] = levels[type];
        return {min, max}
    }

    /**
     * Clamp a zoom value into the range where a data type is visible, so a URL
     * zoom never lands where the referenced feature type would be hidden.
     *
     * Returns the nearest visible zoom: the min when the zoom is below it (all
     * types), and the max when above it only for routes, the single type with an
     * upper visibility bound (the rest stay visible above their max, see
     * showDataType). Unknown types are returned unchanged.
     *
     * @param type Data type (place, hotel, poi, stage, route).
     */
    static clampZoomToVisibility(type: string, zoom: number): number {
        const range = this.getVisibilityZoomLevels()[type as keyof VisibilityZoomLevels];
        if (!Array.isArray(range)) {
            return zoom;
        }
        const [min, max] = range;
        if (zoom < min) {
            return min;
        }
        if (type === dataTypes.route && zoom > max) {
            return max;
        }
        return zoom;
    }

    /**
     * Utility function to log bounds
     *
     * OJO: hoy no lo llama nadie. Las dos ultimas trazas pasaban tres
     * argumentos a debug(), que solo acepta dos, asi que se perdian dy y la
     * altura. Se agrupan en un array para que se registren los dos valores.
     */
    static logBounds(bounds: LngLatBounds, title = "Bounds"): void {
        this.debug(title, bounds);
        const [dx, dy] = this.boundsToDeltaLngLat(bounds);
        this.debug("Diff (lon, lat)", [dx, dy]);
        this.debug("Width, height (m)", [
            bounds.getNorthEast().distanceTo(bounds.getNorthWest()),
            bounds.getNorthEast().distanceTo(bounds.getSouthEast()),
        ]);
    }

    /**
     * Calculate delta for Bouds
     *
     * @return [deltaLng, deltaLat]
     */
    static boundsToDeltaLngLat(bounds: LngLatBounds): [number, number] {
        return [
            bounds.getEast() - bounds.getWest(),
            bounds.getNorth() - bounds.getSouth()
        ];
    }

    /**
     * Expand bounds by a given percentage.
     *
     * @return new bounds object
     */
    static expandBounds(bounds: LngLatBounds, pct = 0.1): GeoBounds {
        const [deltaLng, deltaLat] = this.boundsToDeltaLngLat(bounds);
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        // NE, add delta
        ne.lng += deltaLng * pct;
        ne.lat += deltaLat * pct;

        // SW, substract.
        sw.lng -= deltaLng * pct;
        sw.lat -= deltaLng * pct;

        return new GeoBounds(sw, ne);
    }

    /**
     * Show camino?
     */
    static showCamino(zoom: number): boolean {
        const config = this.config();
        return zoom < config.zoom_level_camino_max;
    }

    /**
     * Show places (localidades) ?
     */
    static showPlaces(zoom: number): boolean {
        const config = this.config();
        return zoom >= config.zoom_level_place_min;
    }

    /**
     * Show accommodations (hotels) ?
     */
    static showHotels(zoom: number): boolean {
        const config = this.config();
        return zoom >= config.zoom_level_accommodation_min;
    }

    /**
     * Show accommodations (hotels) ?
     */
    static showHotelNames(zoom: number): boolean {
        const config = this.config();
        return zoom >= config.zoom_level_accommodation_text_min;
    }

    /**
     * Show stages (etapas) ?
     */
    static showStages(zoom: number): boolean {
        return this.showDataType(dataTypes.stage, zoom);
    }

    /**
     * Show stages (etapas) ?
     */
    static showRoutes(zoom: number): boolean {
        return this.showDataType(dataTypes.route, zoom);
    }


    /**
     * Show arrows ?
     */
    // Kept for symmetry with the other showX(zoom) checks.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    static showArrows(_zoom: number): boolean {
        // Always shown; the zoom limit (config.zoom_level_arrow_max) is not applied.
        return true;
    }


    /**
     * Show track numbers (etapas) ?
     */
    static showNumbers(zoom: number): boolean {
        return !(this.showRoutes(zoom) || !this.showStages(zoom));
    }

    /**
     * Show route names (caminos) ?
     */
    static showRouteNames(zoom: number): boolean {
        const config = this.config();
        return ((config.zoom_level_route_names_min < zoom) &&
            (config.zoom_level_stage[0] > zoom));
    }

    /**
     * Show data type for this zoom level ?
     */
    static showDataType(type: ZoomRangeType, zoom: number): boolean {
        const levels = this.getTypeZoomLevels(type);
        // const levels = this.getVisibilityZoomLevels();
        // const [min, max] = levels[type];
        if (type === dataTypes.route) {
            return levels.min <= zoom && zoom <= levels.max;
        } else {
            return levels.min <= zoom;
        }

    }

    /**
     * Store location.
     *
     * OJO: zoom es opcional en LocationData. Si falta se guarda la cadena
     * "undefined", que es lo que ya ocurria: getStoredLocation solo comprueba
     * ese caso para lon y lat, no para zoom.
     */
    static storeLocation(location: LocationData): void {
        UserStorage.setItem('gronze_lon', location.longitude);
        UserStorage.setItem('gronze_lat', location.latitude);
        UserStorage.setItem('gronze_zoom', location.zoom as Coordinate);
    }

    /**
     * Detects browser and OS to select permissions grant instructions
     *
     */
    static selectUrlInstructionsByBrowser (): string {
        // clean data and reload
        const classList = document.documentElement.classList;
        if (classList.contains('chrome')) {
            return 'https://support.google.com/chrome/answer/142065?hl=es';
        } else if (classList.contains('firefox')) {
            return 'https://support.mozilla.org/es/kb/firefox-comparte-mi-ubicacion-con-sitios-web#w_como-puedo-agregar-soporte-para-geolocalizacion-a-mi-sitio-web';
        } else if (classList.contains('safari')) {
            if (classList.contains('mobile')) {
                return 'https://support.apple.com/es-es/HT207092';
            } else {
                return 'https://support.apple.com/es-es/HT5403';
            }
        }
        return '';
    }

    static isUsingConfigInitialLocation(): boolean {
        if (UrlParamsService.hasLocation())
            return false;

        const storedLocation = this.getStoredLocation();
        if (!storedLocation)
            return true;

        const initLocation = this.getInitLocation();
        return LocationData.isEqual(initLocation, storedLocation);
    }

    /**
     * Gets tha actual location.
     */
    static async getCurrentLocation(): Promise<LatLng | undefined> {
        try {
            if (!AppService.isPwa()) {
                const coordinates = await Geolocation.getCurrentPosition();
                return {lat: coordinates.coords.latitude, lng: coordinates.coords.longitude};
            } else {
                const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: Infinity
                    });
                });
                return {
                    lng: position.coords.longitude,
                    lat: position.coords.latitude,
                };
            }
        } catch (e) {
            this.debug('Error recuperando la geolocalización: el navegador no lo admite', e);
        }
    }
}

export default GeoService;
